import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import {
  mergeDesktopDatePickerSlotProps,
  mergeDesktopDatePickerSlots,
} from 'components/pga-time-picker/desktop-date-picker'
import ChevronLeftIcon from 'components/material-symbol/icons/ChevronLeft'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import EventIcon from 'components/material-symbol/icons/Event'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import themes from 'themes'

const iconProps = { fontSize: 'small', opticalSize: 20 }

const slots = {
  leftArrowIcon: ChevronLeftIcon,
  rightArrowIcon: ChevronRightIcon,
  switchViewIcon: ExpandMoreIcon,
}

const slotProps = {
  leftArrowIcon: iconProps,
  rightArrowIcon: iconProps,
  switchViewIcon: iconProps,
}

const pickerTheme = createTheme(themes.pgaCoach)

const useStyles = makeStyles({
  hiddenDatePicker: {
    width: 0,
    height: 0,
    overflow: 'hidden',
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
  },
})

/**
 * A button showing the selected month that opens a month and year picker in a
 * popover under it. Months after the current one cannot be picked. It uses the
 * same MUI X month and year picker as Payments transaction history.
 */
const MonthPickerButton = ({
  value,
  onChange,
  labelFormat = 'LLL yyyy',
  buttonProps,
}) => {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef(null)

  return (
    <ThemeProvider theme={pickerTheme}>
      <LocalizationProvider dateAdapter={CustomLuxonAdapter}>
        <>
          <Button
            ref={buttonRef}
            variant="outlined"
            startIcon={<EventIcon {...iconProps} />}
            aria-label={`Selected month ${value.toFormat('LLLL yyyy')}`}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            {...buttonProps}
            onClick={() => setIsOpen(true)}
          >
            {value.toFormat(labelFormat)}
          </Button>
          <Box className={classes.hiddenDatePicker}>
            <DesktopDatePicker
              open={isOpen}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              label="Select Month"
              value={value}
              onChange={newValue => {
                if (newValue) onChange(newValue)
                setIsOpen(false)
              }}
              views={['month', 'year']}
              format={labelFormat}
              disableFuture
              maxDate={DateTime.now().endOf('month')}
              slots={mergeDesktopDatePickerSlots(slots)}
              slotProps={mergeDesktopDatePickerSlotProps({
                ...slotProps,
                popper: {
                  anchorEl: () => buttonRef.current,
                  placement: 'bottom-end',
                },
              })}
            />
          </Box>
        </>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

MonthPickerButton.propTypes = {
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  labelFormat: PropTypes.string,
  buttonProps: PropTypes.object,
}

export default MonthPickerButton
